// ShellMobileMenu — the mobile drop-down nav that appears when the hamburger
// is tapped. Extracted out of Layout so the top-level Shell stays under 200
// lines and the mobile nav can evolve independently (search box, pinned
// items, etc.) without touching the shell shell itself.
import { NavLink } from "react-router-dom";

const slug = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-");


function DisabledLink({ label, icon: Icon, indent = 6, testId }) {
  return (
    <div
      data-testid={testId}
      aria-disabled="true"
      className={`flex items-center gap-3 pl-${indent} pr-3 py-${indent >= 10 ? "2.5" : "3"} rounded-lg text-sm text-slate-600 cursor-not-allowed`}
    >
      <Icon className={`w-${indent >= 10 ? 4 : 5} h-${indent >= 10 ? 4 : 5} text-slate-600`} />
      <span className="truncate">{label}</span>
      <span className="ml-auto text-[8.5px] font-mono uppercase tracking-widest px-1 py-0.5 rounded bg-white/[0.03] text-slate-500 border border-white/[0.06]">
        TBD
      </span>
    </div>
  );
}


function ActiveLink({ to, label, icon: Icon, color, indent = 6, testId }) {
  return (
    <NavLink
      to={to}
      end
      data-testid={testId}
      className={({ isActive }) =>
        `flex items-center gap-3 pl-${indent} pr-3 py-${indent >= 10 ? "2.5" : "3"} rounded-lg text-sm font-medium transition-colors ${
          isActive ? "bg-white/10 text-white" : "text-slate-200 hover:bg-white/5 hover:text-white"
        }`
      }
    >
      <Icon className={`w-${indent >= 10 ? 4 : 5} h-${indent >= 10 ? 4 : 5} ${color}`} />
      {label}
    </NavLink>
  );
}


function MobileNavGroup({ group }) {
  const GroupIcon = group.icon;
  return (
    <div className="mt-2 pt-2 border-t border-white/5" data-testid={`mobile-nav-${group.key}-group`}>
      <div className="px-3 pb-1 text-[11px] uppercase tracking-wider text-slate-500 flex items-center gap-2">
        <GroupIcon className="w-3.5 h-3.5" />
        {group.label}
      </div>
      {group.children.map((c) => {
        if (c.type === "subgroup") return <MobileNavSubgroup key={c.key} sub={c} groupKey={group.key} />;
        const testId = `mobile-nav-${group.key}-sub-${slug(c.label)}`;
        return c.disabled
          ? <DisabledLink key={c.to} label={c.label} icon={c.icon} indent={6} testId={testId} />
          : <ActiveLink key={c.to} to={c.to} label={c.label} icon={c.icon} color={c.color} indent={6} testId={testId} />;
      })}
    </div>
  );
}


function MobileNavSubgroup({ sub, groupKey }) {
  const SubIcon = sub.icon;
  return (
    <div className="mt-1 pt-1 border-t border-white/[0.04] ml-4" data-testid={`mobile-nav-${groupKey}-sub-${sub.key}`}>
      <div className="pl-3 pr-3 pb-1 text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-2">
        <SubIcon className={`w-3 h-3 ${sub.color}`} />
        {sub.label}
      </div>
      {sub.children.map((gc) => {
        const testId = `mobile-nav-${groupKey}-sub-${sub.key}-item-${slug(gc.label)}`;
        return gc.disabled
          ? <DisabledLink key={gc.to} label={gc.label} icon={gc.icon} indent={10} testId={testId} />
          : <ActiveLink key={gc.to} to={gc.to} label={gc.label} icon={gc.icon} color={gc.color} indent={10} testId={testId} />;
      })}
    </div>
  );
}


export default function ShellMobileMenu({ user, navItems, onClose }) {
  return (
    <>
      <button
        type="button"
        aria-label="Close menu"
        onClick={onClose}
        className="lg:hidden fixed inset-0 top-14 bg-black/40 backdrop-blur-sm z-40"
        data-testid="mobile-menu-backdrop"
      />
      <div
        id="mobile-nav-panel"
        className="lg:hidden absolute left-0 right-0 top-14 z-50 border-b border-white/5 bg-[#0F1A30]/95 backdrop-blur-xl shadow-2xl max-h-[calc(100vh-3.5rem)] overflow-y-auto"
        data-testid="mobile-nav-panel"
      >
        <nav className="px-4 sm:px-6 py-3 flex flex-col gap-1">
          <div className="sm:hidden px-3 py-2 border-b border-white/5 mb-2">
            <div className="text-sm font-medium text-white">{user?.name}</div>
            <div className="text-[11px] text-slate-400">
              {user?.title || user?.email}
              {user?.role === "admin" && <span className="text-orange-400"> · Admin</span>}
            </div>
          </div>
          {navItems.map((n) => {
            if (n.type === "divider") {
              return (
                <div key={n.key} className="pt-3 pb-1 px-3" data-testid={`mobile-nav-divider-${n.key}`}>
                  <div className="text-[10px] uppercase tracking-widest text-slate-600">{n.label}</div>
                </div>
              );
            }
            if (n.type === "link") {
              const testId = `mobile-nav-${slug(n.label)}`;
              return n.disabled
                ? <DisabledLink key={n.to} label={n.label} icon={n.icon} indent={3} testId={testId} />
                : <ActiveLink key={n.to} to={n.to} label={n.label} icon={n.icon} color={n.color} indent={3} testId={testId} />;
            }
            return <MobileNavGroup key={n.key} group={n} />;
          })}
        </nav>
      </div>
    </>
  );
}
